import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import { fieldErrors } from "utils/apiErrors";
import V2AuthInput from "./components/V2AuthInput";
import V2AuthButton from "./components/V2AuthButton";
import V2PasswordValidation, {
  validatePassword,
} from "./components/V2PasswordValidation";

const getInviteEmail = () => {
  try {
    const pendingUrl = sessionStorage.getItem("pendingInviteUrl");
    if (!pendingUrl) return "";
    const params = new URLSearchParams(pendingUrl.split("?")[1]);
    const token = params.get("token");
    if (!token) return "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.invitee_email || "";
  } catch {
    return "";
  }
};

export default function V2Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Form fields — pre-populate email from invite token if available
  const [email, setEmail] = useState(() => getInviteEmail());
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { allValid } = validatePassword(password, confirmPassword);

  // Form validation
  const isFormValid =
    email.trim() && firstName.trim() && lastName.trim() && allValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    if (!allValid) {
      setErrors({
        password: t("auth.common.passwordRequirementsError"),
      });
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await betaAPI.register({
        body: {
          email,
          password,
          confirm_password: confirmPassword,
          fname: firstName,
          lname: lastName,
        },
      });

      // Store auth data
      localStorage.setItem("authToken", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      // Redirect to email verification
      navigate("/v2/auth/verify-email-sent", { replace: true });
    } catch (err) {
      console.error("Registration error:", err);

      // Field-specific validation errors from the API's problem details.
      const fieldMap = {
        fname: "firstName",
        lname: "lastName",
        confirm_password: "confirmPassword",
      };
      const validationErrors = {};
      fieldErrors(err.payload).forEach(({ field, message }) => {
        const key = field ? fieldMap[field] || field : "general";
        if (!validationErrors[key]) validationErrors[key] = message;
      });
      if (!Object.keys(validationErrors).length) {
        validationErrors.general = err.message || t("auth.register.failed");
      }
      setErrors(validationErrors);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="mb-2 text-xl font-bold text-[#111827]">
          {t("auth.register.title")}
        </h1>
        <p className="text-sm text-[#6B7280]">{t("auth.register.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#374151]">
            {t("auth.common.email")} *
          </label>
          <V2AuthInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("auth.common.emailPlaceholder")}
            error={!!errors.email}
            disabled={isLoading}
            autoComplete="email"
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Name fields */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#374151]">
              {t("auth.register.firstName")} *
            </label>
            <V2AuthInput
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t("auth.register.firstNamePlaceholder")}
              error={!!errors.firstName}
              disabled={isLoading}
              autoComplete="given-name"
            />
            {errors.firstName && (
              <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[#374151]">
              {t("auth.register.lastName")} *
            </label>
            <V2AuthInput
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t("auth.register.lastNamePlaceholder")}
              error={!!errors.lastName}
              disabled={isLoading}
              autoComplete="family-name"
            />
            {errors.lastName && (
              <p className="mt-1 text-xs text-red-600">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#374151]">
            {t("auth.common.password")} *
          </label>
          <V2AuthInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("auth.common.passwordPlaceholder")}
            error={!!errors.password}
            disabled={isLoading}
            autoComplete="new-password"
          />
        </div>

        {/* Confirm Password */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-[#374151]">
            {t("auth.register.confirmPassword")} *
          </label>
          <V2AuthInput
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t("auth.register.confirmPasswordPlaceholder")}
            error={!!errors.confirmPassword}
            disabled={isLoading}
            autoComplete="new-password"
          />
          {errors.confirmPassword && (
            <p className="mt-1 text-xs text-red-600">
              {errors.confirmPassword}
            </p>
          )}
        </div>

        {/* Password validation */}
        <V2PasswordValidation
          password={password}
          confirmPassword={confirmPassword}
        />

        {/* Error messages */}
        {errors.password && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{errors.password}</p>
          </div>
        )}

        {errors.general && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{errors.general}</p>
          </div>
        )}

        {/* Submit button */}
        <V2AuthButton
          type="submit"
          variant="primary"
          disabled={!isFormValid}
          loading={isLoading}
        >
          {t("auth.register.submit")}
        </V2AuthButton>
      </form>

      {/* Login link */}
      <div className="mt-6 text-center">
        <p className="text-sm text-[#6B7280]">
          {t("auth.register.alreadyRegistered")}{" "}
          <Link
            to="/v2/auth/login"
            className="font-medium text-[#E07A3A] transition-colors hover:text-[#D66B2E]"
          >
            {t("auth.register.signIn")}
          </Link>
        </p>
      </div>
    </>
  );
}
